import axios from 'axios';

import { notification } from 'antd';

import { all, takeEvery, put, call } from 'redux-saga/effects';

import { firebaseFirestore, firebaseStorage, firebaseAuth, baseUrl } from 'services/firebase';

import { history } from 'redux/store';

import { intl } from 'utils/intl';
import downloadCSV from 'utils/downloadCSV';

import {
  fetchCampaignsSuccess,
  fetchCampaignDetailsSuccess,
  setCampaignState,
  fetchCampaignGiftsSuccess,
} from './actions';

import CampaignActionTypes from './types';

import {
  getCampaigns,
  getCampaignGifts,
  uploadGiftImages,
  mapCampaignGiftOperatorDocs,
} from './utils';

export function* fetchCampaignsAsync() {
  yield put(setCampaignState({ isFetchingCampaigns: true }));

  try {
    const campaignsRef = yield firebaseFirestore.collection('campaigns');
    const campaignsSnapshot = yield campaignsRef.get();

    if (!campaignsSnapshot.empty) {
      const campaignsQueryRef = yield campaignsRef.orderBy('validFrom', 'desc');
      const campaignsQuerySnapshot = yield campaignsQueryRef.get();

      const campaigns = yield call(getCampaigns, campaignsQuerySnapshot);
      yield put(fetchCampaignsSuccess(campaigns));
    }

    yield put(setCampaignState({ isFetchingCampaigns: false }));
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaigns.notification.fetch.error' }),
    });
    yield put(setCampaignState({ isFetchingCampaigns: false }));
  }
}

export function* saveCampaignAsync({ payload }) {
  const { campaign, campaignImageFile, campaignId } = payload;
  yield put(setCampaignState({ isSavingCampaign: true }));

  try {
    const campaignRef = yield firebaseFirestore.collection('campaigns').doc(campaignId);

    const campaignSnapshot = yield campaignRef.get();

    const imagePath = campaignImageFile
      ? `campaigns/${campaignImageFile.name}`
      : campaignSnapshot.data().image;

    yield campaignRef.set({ ...campaign, image: imagePath }, { merge: true });

    if (campaignImageFile) {
      yield firebaseStorage.ref(imagePath).put(campaignImageFile);
    }

    yield put(setCampaignState({ isSavingCampaign: false }));
    notification.success({
      message: intl.formatMessage({ id: 'campaign.notification.success.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.edit.success' }),
    });
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.edit.error' }),
    });
  }
}

export function* saveNewCampaignAsync({ payload }) {
  const { campaign, campaignImageFile, gifts } = payload;

  const campaignId = campaign.campaignId || '';
  yield put(setCampaignState({ isSavingCampaign: true }));

  try {
    const campaignRef = yield campaignId
      ? firebaseFirestore.collection('campaigns').doc(campaignId)
      : firebaseFirestore.collection('campaigns').doc();
    const campaignGiftsRef = firebaseFirestore.collection('campaignGifts');

    const campaignSnapshot = yield campaignRef.get();

    yield campaignRef.set({ ...campaign, image: campaign.image || '' });

    if (!campaignId) {
      if (campaign.image) {
        yield firebaseStorage.ref(campaign.image).put(campaignImageFile);
      }

      const batch = yield firebaseFirestore.batch();

      gifts.forEach(gift => {
        const newGiftRef = campaignGiftsRef.doc();
        let newGift = {
          campaign: { id: campaignSnapshot.id },
          ...gift,
          description: gift.description || '',
        };

        if (newGift.imageFileName) {
          newGift = { ...newGift, image: gift.imageFileName };
        }

        delete newGift.imageFileName;
        delete newGift.tempId;

        batch.set(newGiftRef, newGift);
      });

      yield batch.commit();

      const filteredGifts = yield gifts.filter(gift => gift.image && gift.image !== '');

      yield call(uploadGiftImages, filteredGifts);
    }

    yield put(setCampaignState({ isSavingCampaign: false }));
    yield history.push('/campaigns');
    notification.success({
      message: intl.formatMessage({ id: 'campaign.notification.success.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.save.success' }),
    });
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.save.error' }),
    });
    yield put(setCampaignState({ isSavingCampaign: false }));
  }
}

export function* fetchCampaignDetailsAsync({ payload }) {
  yield put(setCampaignState({ isFetchingCampaign: true }));
  try {
    const campaignsRef = yield firebaseFirestore.collection('campaigns').doc(payload);
    const snapshot = yield campaignsRef.get();

    const campaign = snapshot.data();
    let imageUrl = '';

    if (campaign.image) {
      try {
        imageUrl = yield firebaseStorage
          .ref()
          .child(campaign.image)
          .getDownloadURL();
      } catch (error) {
        imageUrl = '';
      }
    }

    yield put(fetchCampaignDetailsSuccess({ ...campaign, image: imageUrl }));
    yield put(setCampaignState({ isFetchingCampaign: false }));
  } catch (error) {
    yield put(setCampaignState({ loading: false }));
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.load.error' }),
    });
  }
}

export function* fetchCampaignGiftsAsync({ payload }) {
  yield put(setCampaignState({ isFetchingCampaignGifts: true }));

  try {
    const campaignGiftsRef = yield firebaseFirestore
      .collection('campaignGifts')
      .where('campaign.id', '==', payload);
    const snapshot = yield campaignGiftsRef.get();

    const campaignGifts = yield call(getCampaignGifts, snapshot);

    yield put(fetchCampaignGiftsSuccess(campaignGifts));
    yield put(setCampaignState({ isFetchingCampaignGifts: false }));
  } catch (error) {
    yield put(setCampaignState({ isFetchingCampaignGifts: false }));
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.load.gifts.error' }),
    });
  }
}

export function* saveCamapignGiftAsync({ payload: { gift, file } }) {
  yield put(setCampaignState({ isSavingCampaignGift: true }));

  try {
    const campaignGiftRef = yield gift.id
      ? firebaseFirestore.collection('campaignGifts').doc(gift.id)
      : firebaseFirestore.collection('campaignGifts').doc();
    const campaignGiftSnapshot = yield campaignGiftRef.get();

    const campaignGiftsRef = yield firebaseFirestore
      .collection('campaignGifts')
      .where('campaign.id', '==', gift.campaign.id);

    const campaignGiftsSnapshot = yield campaignGiftsRef.get();

    let isMagicNumberAvailable = true;

    campaignGiftsSnapshot.docs.forEach(snapshot => {
      if (gift.id && snapshot.id !== gift.id && snapshot.data().magicNumber === gift.magicNumber) {
        isMagicNumberAvailable = false;
      } else if (!gift.id && snapshot.data().magicNumber === gift.magicNumber) {
        isMagicNumberAvailable = false;
      }
    });

    if (!isMagicNumberAvailable) {
      notification.error({
        message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
        description: intl.formatMessage({ id: 'campaign.notification.magicNumber.error' }),
      });
      yield put(setCampaignState({ isSavingCampaignGift: false }));
      return;
    }

    if (gift.id) {
      const tempGift = { ...gift };
      delete tempGift.id;

      const image =
        campaignGiftSnapshot.data().image || (file && file.name ? `gifts/${file.name}` : '');

      yield campaignGiftRef.set({ ...tempGift, image }, { merge: true });
    } else {
      const image = (file && `gifts/${file.name}`) || '';
      yield campaignGiftRef.set(
        { ...gift, description: gift.description || '', image },
        { merge: true },
      );
    }

    if (file) {
      yield firebaseStorage
        .ref(
          gift.id
            ? campaignGiftSnapshot.data().image || (file ? `gifts/${file.name}` : '')
            : `gifts/${file.name}`,
        )
        .put(file);
    }

    yield put(setCampaignState({ isSavingCampaignGift: false, didSaveCampaignGift: true }));
  } catch (error) {
    yield put(setCampaignState({ isSavingCampaignGift: false }));
    console.log(error);
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.save.gift.error' }),
    });
  }
}

export function* changeCampaignPublishingStatus({ payload }) {
  yield put(setCampaignState({ isPublishingCampaign: true }));

  const { campaignId, willPublish } = payload;

  try {
    const campaignRef = yield firebaseFirestore.collection('campaigns').doc(campaignId);

    yield campaignRef.set(
      {
        isPublished: willPublish,
      },
      { merge: true },
    );

    yield put(setCampaignState({ isPublishingCampaign: false, didPublishCampaign: true }));
  } catch (error) {
    yield put(setCampaignState({ isPublishingCampaign: false }));
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({
        id: !willPublish
          ? 'campaign.notification.unpublish.error'
          : 'campaign.notification.publish.error',
      }),
    });
  }
}

export function* generateCampaignQRCodes({ payload }) {
  const { campaignId, amount } = payload;

  yield put(setCampaignState({ isGeneratingCampaignQRCodes: true }));

  try {
    const token = yield firebaseAuth.currentUser.getIdToken();
    const userId = yield firebaseAuth.currentUser.uid;
    const url = `${baseUrl}/generateQrCodesForCampaign?campaignId=${campaignId}&caller=${userId}&amount=${amount}`;

    const result = yield axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

    if (result && result.data) {
      notification.success({
        message: intl.formatMessage({ id: 'campaign.notification.success.title' }),
        description: intl.formatMessage({ id: 'campaign.notification.generation.success' }),
      });
      yield put(
        setCampaignState({ isGeneratingCampaignQRCodes: false, didGenerateCampaignQRCodes: true }),
      );
    }
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.generation.error' }),
    });
    yield put(setCampaignState({ isGeneratingCampaignQRCodes: false }));
  }
}

export function* generateLoosersReportForCampaignAsync({ payload }) {
  const { campaignId } = payload;

  yield put(setCampaignState({ isGeneratingLoosersForCampaign: true }));

  try {
    const token = yield firebaseAuth.currentUser.getIdToken();
    const url = `${baseUrl}/generateLoosersReportForCampaign?campaignId=${campaignId}`;

    const result = yield axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

    if (result && result.data) {
      notification.success({
        message: intl.formatMessage({ id: 'campaign.notification.success.title' }),
        description: intl.formatMessage({ id: 'campaign.notification.generation.success' }),
      });
      yield put(setCampaignState({ isGeneratingLoosersForCampaign: false }));

      yield call(downloadCSV, {
        filename: 'non-winning-users.csv',
        data: result.data.data,
        columns: ['name', 'surname', 'phone'],
      });
    }
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.generation.error' }),
    });
    yield put(setCampaignState({ isGeneratingLoosersForCampaign: false }));
  }
}

export function* deleteCampaignGift({ payload }) {
  const { giftId } = payload;

  yield put(setCampaignState({ isDeletingCampaignGift: true }));

  try {
    yield firebaseFirestore
      .collection('campaignGifts')
      .doc(giftId)
      .delete();
    yield put(
      setCampaignState({
        isDeletingCampaignGift: false,
        hasDeletedCampaignGift: true,
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.delete.error' }),
    });
    yield put(setCampaignState({ isDeletingCampaignGift: false }));
  }
}

export function* fetchCampaignGiftOperators() {
  yield put(setCampaignState({ isFetchingCampaignGiftOperators: true }));

  try {
    const operatorsRef = yield firebaseFirestore.collection('mobileOperators');
    const operatorsSnapshot = yield operatorsRef.get();

    const operators = yield call(mapCampaignGiftOperatorDocs, operatorsSnapshot.docs);

    yield put(
      setCampaignState({
        isFetchingCampaignGiftOperators: false,
        campaignGiftOperators: operators,
        hasFetchedCampaignGiftOperators: true,
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
      description: intl.formatMessage({ id: 'campaign.notification.gift.operators.error' }),
    });
    yield put(setCampaignState({ isFetchingCampaignGiftOperators: false }));
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(CampaignActionTypes.FETCH_CAMPAIGNS_START, fetchCampaignsAsync),
    takeEvery(CampaignActionTypes.FETCH_CAMPAIGN_DETAILS_START, fetchCampaignDetailsAsync),
    takeEvery(CampaignActionTypes.FETCH_CAMPAIGN_GIFTS_START, fetchCampaignGiftsAsync),
    takeEvery(CampaignActionTypes.SAVE_CAMPAIGN_GIFT_START, saveCamapignGiftAsync),
    takeEvery(CampaignActionTypes.SAVE_CAMPAIGN_START, saveCampaignAsync),
    takeEvery(CampaignActionTypes.SAVE_NEW_CAMPAIGN_START, saveNewCampaignAsync),
    takeEvery(CampaignActionTypes.GENERATE_CAMPAIGN_QR_CODES, generateCampaignQRCodes),
    takeEvery(
      CampaignActionTypes.GENERATE_LOOSERS_REPORT_FOR_CAMPAIGN,
      generateLoosersReportForCampaignAsync,
    ),
    takeEvery(
      CampaignActionTypes.CHANGE_CAMPAIGN_PUBLISHING_STATUS,
      changeCampaignPublishingStatus,
    ),
    takeEvery(CampaignActionTypes.DELETE_CAMPAIGN_GIFT, deleteCampaignGift),
    takeEvery(CampaignActionTypes.FETCH_CAMPAIGN_GIFT_OPERATORS, fetchCampaignGiftOperators),
  ]);
}
